// src/utils/http.js
// —— 统一 axios 实例 + 版本头检测（X-App-Version）
// 逻辑：只要有任何一次 API 返回的版本号与本地不一致 => 静默清理缓存并强制刷新
// 说明：不新增探测请求，不做定时轮询；仅在“已有请求的响应”上做一次性判断

import axios from 'axios';
import { message } from 'antd';

// 与后端约定的响应头名称（注意 axios 统一会用小写键）
const VERSION_HEADER = 'x-app-version';
// 与入口 index.js 一致的本地版本号存储键
const LS_KEY = 'app_version';

// 避免并发多次触发清理与刷新
let handledVersionMismatch = false;
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const DEFAULT_READ_TIMEOUT_MS = 10 * 60 * 1000;
const DEFAULT_WRITE_TIMEOUT_MS = 20 * 1000;
const POOL_BUSY_RETRY_BACKOFF_MS = [3000, 8000];
const POOL_BUSY_RETRY_FLAG = '__poolBusyRetryAttempt';
const POOL_BUSY_RETRYABLE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const OVERLOAD_ALERT_THROTTLE_MS = 10 * 1000;
const OVERLOAD_ALERT_KEY = 'overload-limit-503';
const RETRY_BLOCK_REASONS = new Set([
  'DEGRADED_HALF_BLOCK',
  'FULL_BLOCK_NON_SPECIAL',
  'SIMULATE_DISABLED_IN_DEGRADED',
  'SERVER_MODE_TECHNICAL_OFFICER_BLOCK',
  'SERVER_MODE_SUPER_ADMIN_BLOCK',
]);
const OVERLOAD_ALERT_REASONS = new Set([
  'DEGRADED_HALF_BLOCK',
  'FULL_BLOCK_NON_SPECIAL',
  'SIMULATE_DISABLED_IN_DEGRADED',
]);
let lastOverloadAlertAt = 0;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms) || 0)));
}

function getRetryAfterMs(error) {
  const retryAfterRaw = error?.response?.headers?.['retry-after'];
  const retryAfterSec = Number(retryAfterRaw);
  if (!Number.isFinite(retryAfterSec) || retryAfterSec <= 0) return 0;
  return Math.floor(retryAfterSec * 1000);
}

function shouldRetryOnPoolBusy(error) {
  const status = Number(error?.response?.status);
  if (status !== 503) return false;
  const config = error?.config || {};
  if (config?.__skipPoolBusyRetry) return false;
  const method = String(config?.method || '').trim().toUpperCase();
  const allowRetryForMethod = config?.__allowPoolBusyRetry === true
    || POOL_BUSY_RETRYABLE_METHODS.has(method);
  if (!allowRetryForMethod) return false;
  const body = error?.response?.data || {};
  const reason = String(body?.reason || '').trim();
  if (RETRY_BLOCK_REASONS.has(reason)) return false;
  const retryAfterMs = getRetryAfterMs(error);
  const poolBusyByBody = body?.poolBusy === true || Number(body?.code) === 503;
  return poolBusyByBody || retryAfterMs > 0;
}

function shouldNotifyOverloadLimit(error) {
  const status = Number(error?.response?.status);
  if (status !== 503) return false;
  const body = error?.response?.data || {};
  const reason = String(body?.reason || '').trim();
  return OVERLOAD_ALERT_REASONS.has(reason);
}

function notifyOverloadLimit() {
  const now = Date.now();
  if (now - lastOverloadAlertAt < OVERLOAD_ALERT_THROTTLE_MS) return;
  lastOverloadAlertAt = now;
  message.warning({
    key: OVERLOAD_ALERT_KEY,
    duration: 3,
    content: '由于服务器访问压力过大，您已被临时限流，请稍后再试。',
  });
}

/** 一次性清理并强制刷新为最新版本 */
async function hardReloadAndClear(serverVersion) {
  if (handledVersionMismatch) return;
  handledVersionMismatch = true;

  try {
    // 轻提示（不阻塞）
    message.loading({ content: '检测到新版本，正在为您更新...', key: 'ver', duration: 1.2 });

    // 1) 清空 localStorage（与入口逻辑对齐）
    localStorage.clear();

    // 2) 清理 Cache API（若可用）
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      } catch (e) {}
    }

    // 3) 保存服务器版本，避免刷新后入口 index.js 再次清理导致“二连跳”
    if (serverVersion) {
      localStorage.setItem(LS_KEY, serverVersion);
    }

    // 4) 注销所有 Service Worker（避免旧缓存干扰）
    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      } catch (e) {}
    }
  } finally {
    // 5) 强制刷新（尽量绕过缓存）
    // 某些浏览器对 true 已忽略，但无妨；入口脚本会再次校验
    window.location.reload(true);
  }
}

/** 创建专用 axios 实例（替代原先的全局 defaults） */
const instance = axios.create({
  // 统一在 request 拦截器里按读/写设置默认超时
  timeout: 0,
  withCredentials: true, // 后端 CORS 需 credentials=true
  // baseURL: process.env.REACT_APP_API_BASE_URL || '/api', // 如需可启用
});

instance.interceptors.request.use((config) => {
  const method = String(config?.method || 'GET').trim().toUpperCase();
  const timeout = Number(config?.timeout);
  const hasExplicitTimeout = Number.isFinite(timeout) && timeout > 0;
  if (!hasExplicitTimeout) {
    config.timeout = READ_METHODS.has(method)
      ? DEFAULT_READ_TIMEOUT_MS
      : DEFAULT_WRITE_TIMEOUT_MS;
  }
  return config;
});

// （可选，仅开发调试）打印后端的限流观测头，便于定位
// instance.interceptors.response.use((res) => {
//   if (process.env.NODE_ENV !== 'production') {
//     const h = res.headers || {};
//     console.debug('[rate-limit]', h['x-ratelimit-policy'], h['x-client-ip'], h['x-proxy-chain'], h['x-auth-debug']);
//   }
//   return res;
// });

/** —— 响应拦截器：读取后端版本号并对比 —— */
instance.interceptors.response.use(
  (response) => {
    // 读服务端回传的版本号
    const h = response?.headers || {};
    const serverVer =
      h[VERSION_HEADER] || h['X-App-Version'] || h['x-app-version'];

    if (serverVer) {
      const localVer = localStorage.getItem(LS_KEY);

      // 如已有本地版本且与服务端不一致：当前页仍在旧前端上 → 清理并刷新
      if (localVer && localVer !== serverVer && !handledVersionMismatch) {
        // 按你的偏好：不弹确认，直接静默刷新
        hardReloadAndClear(serverVer);
      }
    }
    return response;
  },
  (error) => {
    // 错误响应同样检查头部（如 4xx/5xx）
    try {
      const h = error?.response?.headers || {};
      const serverVer =
        h[VERSION_HEADER] || h['X-App-Version'] || h['x-app-version'];
      const localVer = localStorage.getItem(LS_KEY);
      if (serverVer && localVer && localVer !== serverVer && !handledVersionMismatch) {
        hardReloadAndClear(serverVer);
      }
    } catch (e) {}

    if (shouldRetryOnPoolBusy(error)) {
      const cfg = error?.config || null;
      if (cfg) {
        const attempt = Number(cfg[POOL_BUSY_RETRY_FLAG] || 0);
        if (attempt < POOL_BUSY_RETRY_BACKOFF_MS.length) {
          cfg[POOL_BUSY_RETRY_FLAG] = attempt + 1;
          const delayMs = Math.max(
            POOL_BUSY_RETRY_BACKOFF_MS[attempt],
            getRetryAfterMs(error),
          );
          return sleep(delayMs).then(() => instance.request(cfg));
        }
      }
    }

    if (shouldNotifyOverloadLimit(error)) {
      notifyOverloadLimit();
    }

    return Promise.reject(error);
  }
);

export default instance;
